import { BlendMode, LayerState, TextData } from '../types';

export class Layer {
  public id: string;
  public name: string;
  public type: 'raster' | 'text';
  public canvas: HTMLCanvasElement;
  public ctx: CanvasRenderingContext2D;
  public visible: boolean = true;
  public locked: boolean = false;
  public opacity: number = 1.0;
  public blendMode: BlendMode = 'source-over';
  public x: number = 0;
  public y: number = 0;
  public width: number;
  public height: number;
  public textData?: TextData;

  constructor(
    width: number,
    height: number,
    name: string = 'Layer 1',
    type: 'raster' | 'text' = 'raster',
    id?: string
  ) {
    this.id = id || 'layer_' + Math.random().toString(36).substring(2, 9);
    this.name = name;
    this.type = type;
    this.width = width;
    this.height = height;

    this.canvas = document.createElement('canvas');
    this.canvas.width = Math.max(1, width);
    this.canvas.height = Math.max(1, height);
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Could not get 2D canvas context');
    this.ctx = ctx;
  }

  public clone(): Layer {
    const copy = new Layer(this.width, this.height, this.name + ' copy', this.type);
    copy.visible = this.visible;
    copy.locked = this.locked;
    copy.opacity = this.opacity;
    copy.blendMode = this.blendMode;
    copy.x = this.x;
    copy.y = this.y;
    if (this.textData) {
      copy.textData = { ...this.textData };
    }
    copy.ctx.drawImage(this.canvas, 0, 0);
    return copy;
  }

  public renderText() {
    if (this.type !== 'text' || !this.textData) return;
    const { text, fontFamily, fontSize, color, bold, italic, align, strokeColor, strokeWidth, shadowColor, shadowBlur } = this.textData;
    this.ctx.clearRect(0, 0, this.width, this.height);

    this.ctx.save();
    let fontStyle = '';
    if (italic) fontStyle += 'italic ';
    if (bold) fontStyle += 'bold ';
    this.ctx.font = `${fontStyle}${fontSize}px ${fontFamily}, sans-serif`;
    this.ctx.textAlign = align;
    this.ctx.textBaseline = 'top';

    if (shadowColor && shadowBlur) {
      this.ctx.shadowColor = shadowColor;
      this.ctx.shadowBlur = shadowBlur;
      this.ctx.shadowOffsetX = 2;
      this.ctx.shadowOffsetY = 2;
    }

    const lines = text.split('\n');
    const lineHeight = fontSize * 1.25;

    lines.forEach((line, index) => {
      let drawX = 0;
      if (align === 'center') drawX = this.width / 2;
      else if (align === 'right') drawX = this.width;

      const drawY = index * lineHeight;

      if (strokeColor && strokeWidth && strokeWidth > 0) {
        this.ctx.strokeStyle = strokeColor;
        this.ctx.lineWidth = strokeWidth;
        this.ctx.strokeText(line, drawX, drawY);
      }

      this.ctx.fillStyle = color;
      this.ctx.fillText(line, drawX, drawY);
    });

    this.ctx.restore();
  }

  public resize(newWidth: number, newHeight: number, keepContent: boolean = true) {
    if (newWidth <= 0 || newHeight <= 0) return;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = this.width;
    tempCanvas.height = this.height;
    const tempCtx = tempCanvas.getContext('2d');
    if (tempCtx && keepContent) {
      tempCtx.drawImage(this.canvas, 0, 0);
    }

    this.width = newWidth;
    this.height = newHeight;
    this.canvas.width = newWidth;
    this.canvas.height = newHeight;

    if (keepContent && tempCtx) {
      this.ctx.drawImage(tempCanvas, 0, 0);
    }
    if (this.type === 'text') {
      this.renderText();
    }
  }

  public getThumbnail(thumbWidth: number = 44, thumbHeight: number = 44): string {
    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.width = thumbWidth;
    thumbCanvas.height = thumbHeight;
    const tCtx = thumbCanvas.getContext('2d');
    if (!tCtx) return '';

    // Draw checkerboard background for transparency
    const patternSize = 6;
    for (let py = 0; py < thumbHeight; py += patternSize) {
      for (let px = 0; px < thumbWidth; px += patternSize) {
        tCtx.fillStyle = ((px / patternSize + py / patternSize) % 2 === 0) ? '#2a2e39' : '#1e222d';
        tCtx.fillRect(px, py, patternSize, patternSize);
      }
    }

    // Scale aspect ratio
    const scale = Math.min(thumbWidth / this.width, thumbHeight / this.height);
    const sw = this.width * scale;
    const sh = this.height * scale;
    const ox = (thumbWidth - sw) / 2;
    const oy = (thumbHeight - sh) / 2;

    tCtx.drawImage(this.canvas, ox, oy, sw, sh);
    return thumbCanvas.toDataURL();
  }

  public clear() {
    this.ctx.clearRect(0, 0, this.width, this.height);
  }

  public fill(color: string) {
    this.ctx.save();
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.width, this.height);
    this.ctx.restore();
  }

  public getState(): LayerState {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      visible: this.visible,
      locked: this.locked,
      opacity: this.opacity,
      blendMode: this.blendMode,
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      textData: this.textData ? { ...this.textData } : undefined
    };
  }
}
